/**
 * 任务状态(海报 + 视频)的**单一来源** — 标签 + 语义色调 + 徽标 class。
 *
 * 此前海报/视频各写一份状态映射(共 4 处,且 RENDERING 等已轻微漂移):
 *   VideoHomePage.STATUS_LABEL / VideoTaskDetailPage.STATUS_LABEL /
 *   PosterDashboardPage.statusBadgeClass / PosterTaskDetailPage.StatusBadge.map
 * 真正重复的是「状态 → 色调」的领域知识,这里收口。各页保留自己的标签语言(视频中文 / 海报英文 pill)
 * 与视觉风格(实心 cover 徽标 / 描边 pill),只共享分类与色调→class 的映射。
 */

export type TaskStatusTone = "success" | "warning" | "danger" | "running" | "neutral";

/**
 * ★`media_task.status` 的**全部取值** — 三线(海报/视频/AI分身)共用一列,故只有一套词汇(2026-07-16)。
 *
 * 事实来源是后端 `MediaTaskService` 的 7 个常量;`MediaTaskStatusVocabularyTest`(api 侧)
 * 钉住那 7 个常量不许悄悄增减,并指回这里。两边各一条测试,谁改了都会被提醒改另一边。
 *
 * 此前**同一列有四套词汇**,而且互相矛盾:
 *   - 后端 7 态(这里这套,唯一真的)
 *   - poster 的 TaskStatus:6 态,**漏 BLOCKED** —— 被拦截的海报任务在 TS 里根本没有对应类型
 *   - video 的 VideoStatus:7 真 + 4 个后端根本不产的 legacy 态(SCRIPTING/COMPOSING/…)
 *   - api-spec.yaml 的 TaskStatus:`[PENDING, PROCESSING, COMPLETED, FAILED]` —— 纯属虚构,
 *     PROCESSING/COMPLETED 在代码里一个都不存在;好在没人 import 它(见 posterService 的 D-10 备注)
 *
 * 那 4 个 legacy 态的代价不是「多几行没用的字符串」:三处在途判定照着它们各写一份本地 Set,
 * 全都漏了 RUNNING → worker 一认领任务,轮询集体停摆(2026-07-15 修列表那份时没搜同款,
 * 详情页两份又漏了一轮)。词汇散着,漏就必然反复发生。
 *
 * ⚠️ `COMPOSING`/`GENERATING` 在**草稿出帧**(`frameGenStatus`)里是另一套活着的词汇,与本列无关 ——
 * 它全程在 VideoCreatePage 本地处理,不经这里。别因为「名字见过」就把它并进来。
 */
export const MEDIA_TASK_STATUSES = [
  "PENDING",
  "RUNNING",
  "PARTIAL",
  "SUCCESS",
  "FAILED",
  "CANCELLED",
  "BLOCKED",
] as const;

export type MediaTaskStatus = (typeof MEDIA_TASK_STATUSES)[number];

/** 状态 → 中文标签。键集必须恰好等于 {@link MEDIA_TASK_STATUSES}(由 taskStatus.test.ts 钉死)。 */
export const TASK_STATUS_LABEL: Record<MediaTaskStatus, string> = {
  PENDING: "排队中",
  RUNNING: "处理中",
  SUCCESS: "已完成",
  PARTIAL: "部分成功",
  FAILED: "失败",
  BLOCKED: "已拦截",
  CANCELLED: "已取消",
};

export function taskStatusLabel(status: string): string {
  // 全中文化(Batch 0.5):未知状态兜「未知状态」,不许裸英文枚举漏到用户面。
  return TASK_STATUS_LABEL[status as MediaTaskStatus] ?? "未知状态";
}

const TONE_BY_STATUS: Record<MediaTaskStatus, TaskStatusTone> = {
  SUCCESS: "success",
  PARTIAL: "warning",
  FAILED: "danger",
  BLOCKED: "danger",
  CANCELLED: "neutral",
  PENDING: "running",
  RUNNING: "running",
};

export function taskStatusTone(status: string): TaskStatusTone {
  return TONE_BY_STATUS[status as MediaTaskStatus] ?? "neutral";
}

/**
 * ★在途判定(是否该继续轮询)的**单一来源**(2026-07-16)。
 *
 * <p>此前三处各写一份本地 ACTIVE_STATUSES —— VideoHomePage / useVideoTaskPolling /
 * VideoTaskDetailPage —— 且**三份都漏了 `RUNNING`**,却都列着 SCRIPTING/COMPOSING/RENDERING 那串
 * <b>后端根本不产</b>的 legacy 态。
 *
 * <p>后果:任务被 media worker CAS 认领(PENDING→RUNNING)的那一刻,三处轮询全部停摆 ——
 * 列表卡和详情页都冻在「处理中」直到用户手动刷新。列表那份 2026-07-15 已修,当时没搜同款,
 * 于是详情页那两份又漏了一轮 —— 这正是「词汇散着就会反复漏」的实证。
 */
export function isTaskActive(status: string): boolean {
  return taskStatusTone(status) === "running";
}

/**
 * 压在封面图上的状态徽标(作品卡用)。
 *
 * ★2026-07-24 H-B 门实测改口径:原本按 tone 上半透明彩底 + 白字(`bg-success/70 text-white`),
 * 实测对比度 **standard 2.21:1 / cyber 2.95:1 / comic 1.58:1**,三皮肤全线不过 AA(小字要 4.5)。
 * 根因是「半透明彩底 + 白字」本身就是个矛盾:底色越浅越亮,白字越糊;而它压在**任意封面图**
 * 上,底色还不受控。
 *
 * 改法:统一深色蒙版 + 白字(对任意封面都稳),语义色退到一个**小圆点**上 ——
 * 语义不丢、也不违反「不能只靠颜色传达信息」(文字标签一直在)。
 */
export function taskStatusSolidBadgeClass(): string {
  return "bg-black/70 text-white backdrop-blur-sm";
}

/** 状态语义点的颜色 class(配合深色蒙版徽标使用)。 */
const DOT_CLASS: Record<TaskStatusTone, string> = {
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-destructive",
  running: "bg-info",
  neutral: "bg-muted-foreground",
};

export function taskStatusDotClass(status: string): string {
  return DOT_CLASS[taskStatusTone(status)];
}

/** 描边 pill(详情页用):tone → 描边 + 浅底 class。 */
const BORDERED_CLASS: Record<TaskStatusTone, string> = {
  success: "border-success/40 bg-success/10 text-success",
  warning: "border-warning/40 bg-warning/10 text-warning",
  danger: "border-destructive/50 bg-destructive/10 text-destructive",
  running: "border-info/40 bg-info/10 text-info",
  neutral: "border-border bg-muted text-muted-foreground",
};

export function taskStatusBorderedBadgeClass(status: string): string {
  return BORDERED_CLASS[taskStatusTone(status)];
}
